import React, { forwardRef, useImperativeHandle, useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { Badge, FormSection } from '@praktio/common-ui';

import { Table } from '../../common/Table/Table';
import CourseTitle from '../../common/CourseTitle/CourseTitle';
import { RootState } from '../../../ducks/store';
import { Roles } from '../../../utils/constants';
import { updateTeamLearnerAssignments } from '../../../ducks/teams';
import { updateSearchParams } from '../../../utils/functions';
import { Col, Row } from 'react-bootstrap';
import { getCourseReport } from '../../../ducks/courses';

// TODO: Figure out a better way to know what course is Ontra (maybe service side flag?)
const OntraString = 'Ontra';

const TeamLearnersHeaders = [
  {
    Header: 'First Name',
    accessor: 'firstName',
    className: 'TeamLearners-first-name',
    minWidth: 150,
    maxWidth: 300,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  {
    Header: 'Last Name',
    accessor: 'lastName',
    className: 'TeamLearners-last-name',
    minWidth: 150,
    maxWidth: 300,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  {
    Header: 'Email',
    accessor: 'email',
    className: 'TeamLearners-email',
    Cell: (props: any) => {
      const original = props.row.original;
      const email = original.email;
      const customDataForCell = props.customDataForCell;
      const editingLearnerAssignments = customDataForCell.editingLearnerAssignments;
      const learnerAssignedToTeam = customDataForCell.learnerAssignedToTeam;
      const onLearnerAssignmentChange = customDataForCell.onLearnerAssignmentChange;

      return (
        <div className="LearnersEmailCell d-flex justify-content-between">
          <span>{email ? email : ''}</span>
          {editingLearnerAssignments && (
            <span
              className="material-icons edit-assignment-status-button pointer"
              onClick={() => {
                if (onLearnerAssignmentChange) {
                  onLearnerAssignmentChange(original, learnerAssignedToTeam ? false : true);
                }
              }}
            >
              {learnerAssignedToTeam ? 'close' : 'add'}
            </span>
          )}
        </div>
      );
    },
  },
  {
    Header: 'Courses',
    accessor: 'courses',
    className: 'TeamLearners-courses',
    Cell: (props: any) => {
      const customDataForCell = props.customDataForCell;
      const coursesAssignedToTeam = customDataForCell.coursesAssignedToTeam as ICourse[];
      const onLearnerCourseAssignmentChange = customDataForCell.onLearnerCourseAssignmentChange;
      const teamId = customDataForCell.teamId;
      const user = props.row.original as IUser;
      const usersUnassignedCourses = user.unassignedCourses;
      const adjustingCourseAssignments = customDataForCell.adjustingCourseAssignments;
      const dispatch = customDataForCell.dispatch;

      return (
        <div className="Courses-badge-container d-flex flex-wrap">
          {sortBy(coursesAssignedToTeam, 'title').map((course) => {
            const courseUnassigned =
              usersUnassignedCourses &&
              usersUnassignedCourses.find(
                (unassignedCourse) => unassignedCourse.courseId === course._id,
              );
            const courseUnassignedViaTeam = teamId && courseUnassigned?.teamId === teamId;
            return (
              <Badge className="outlined d-flex" halfOpacity={courseUnassignedViaTeam}>
                <CourseTitle
                  course={course}
                  className={courseUnassignedViaTeam ? 'half-opacity' : undefined}
                />
                {course?.title?.startsWith(OntraString) && (
                  <span
                    className="material-icons-outlined"
                    onClick={() => {
                      dispatch(
                        getCourseReport({
                          courseId: course._id as string,
                          userId: user._id as string,
                        }),
                      );
                    }}
                  >
                    task
                  </span>
                )}
                {adjustingCourseAssignments && (
                  <div className="d-flex align-items-center pointer">
                    <span
                      className="material-icons"
                      onClick={() => {
                        if (onLearnerCourseAssignmentChange) {
                          onLearnerCourseAssignmentChange(
                            user._id,
                            teamId,
                            course,
                            !courseUnassignedViaTeam,
                          );
                        }
                      }}
                    >
                      {courseUnassignedViaTeam ? 'add' : 'close'}
                    </span>
                  </div>
                )}
              </Badge>
            );
          })}
        </div>
      );
    },
  },
];
interface ITeamLearnersTabProps {
  adjustingCourseAssignments: boolean;
  editingLearnerAssignments: boolean;
  notifyHasChanges: (changes: boolean) => void;
  setAdjustingCourseAssignments: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingLearnerAssignments: React.Dispatch<React.SetStateAction<boolean>>;
  setRightPaneOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSavedChanges: (success: boolean) => void;
}

const TeamLearnersTab = forwardRef<IForwardProps, ITeamLearnersTabProps>(
  (
    {
      adjustingCourseAssignments,
      editingLearnerAssignments,
      notifyHasChanges,
      setAdjustingCourseAssignments,
      setEditingLearnerAssignments,
      setRightPaneOpen,
      setSavedChanges,
    },
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      save,
      cancel,
    }));
    const params = useParams();
    let [searchParams, setSearchParams] = useSearchParams();
    const selectedUser = searchParams.get('userId') || undefined;
    const teamId = params.teamId as string;

    const dispatch = useDispatch();
    const { teamCourses, teamLearnersInitial, nonTeamLearnersInitial } = useSelector(
      (state: RootState) => {
        const { teams, courses, users } = state;
        const { current } = teams;
        const team = cloneDeep(current);
        const allUsers = cloneDeep(users.list);
        const teamCourses: ICourse[] = [];

        (courses.list as ICourse[])?.forEach((course) => {
          const teamHasCourse = !!(team?.courses as ICourse[])?.find(
            (teamCourse) => teamCourse._id === course._id,
          );
          if (teamHasCourse) {
            teamCourses.push(course);
          }
        });

        const teamUsers: IUser[] = team?.users || [];
        const teamLearnersInitial: IUser[] = teamUsers.filter((user) => {
          return user.roles?.find((role) => role.teamId === teamId && role.role === Roles.Learner);
        });
        // TODO - create a high-level function that can filter out these users based on an arg.
        // (so we can use it with the managers as well)
        const nonTeamLearnersInitial: IUser[] =
          allUsers?.filter(
            (userToCheck) =>
              !teamLearnersInitial.some((teamLearner) => userToCheck._id === teamLearner._id),
          ) || [];

        return { teamCourses, teamLearnersInitial, nonTeamLearnersInitial };
      },
    );

    const [courseAssignmentUpdates, setCourseAssignmentUpdates] = useState<
      ILearnerCourseAssignment[]
    >([]);
    const [usersToUnassign, setUsersToUnassign] = useState<string[]>([]);
    const [usersToAssign, setUsersToAssign] = useState<string[]>([]);
    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
    const [assignedLearners, setAssignedLearners] = useState<IUser[]>(teamLearnersInitial);
    const [unassignedLearners, setUnassignedLearners] = useState<IUser[]>(nonTeamLearnersInitial);
    const [hasChanges, setHasChanges] = useState(false);
    const prevTeamLearners = useRef<IUser[] | undefined>();
    const prevUnassignedLearners = useRef<IUser[]>(nonTeamLearnersInitial);
    const prevHasChanges = useRef<boolean>(hasChanges);

    useEffect(() => {
      if (
        (teamLearnersInitial && !isEqual(prevTeamLearners.current, teamLearnersInitial)) ||
        (nonTeamLearnersInitial && !isEqual(prevUnassignedLearners.current, nonTeamLearnersInitial))
      ) {
        setAssignedLearners(teamLearnersInitial);
        setUnassignedLearners(nonTeamLearnersInitial);
        setUsersToAssign([]);
        setUsersToAssign([]);
        if (notifyHasChanges) {
          setHasChanges(false);
          notifyHasChanges(false);
        }
      }
      prevTeamLearners.current = cloneDeep(teamLearnersInitial);
      prevUnassignedLearners.current = cloneDeep(nonTeamLearnersInitial);
    }, [teamLearnersInitial, nonTeamLearnersInitial, notifyHasChanges]);

    useEffect(() => {
      if (
        usersToUnassign.length > 0 ||
        usersToAssign.length > 0 ||
        courseAssignmentUpdates.length > 0
      ) {
        setHasChanges(true);
        notifyHasChanges(true);
      } else {
        setHasChanges(false);
        notifyHasChanges(false);
      }
    }, [usersToUnassign, usersToAssign, courseAssignmentUpdates, notifyHasChanges]);

    useEffect(() => {
      setHiddenColumns(editingLearnerAssignments ? ['courses'] : []);
      if (!editingLearnerAssignments) {
        setAssignedLearners(prevTeamLearners.current || []);
        setUnassignedLearners(prevUnassignedLearners.current);
      }
    }, [editingLearnerAssignments]);

    useEffect(() => {
      if (prevHasChanges.current === false && hasChanges === true) {
        setSavedChanges(false);
      }
      prevHasChanges.current = hasChanges;
    }, [hasChanges, setSavedChanges]);

    const save = async () => {
      try {
        await dispatch(
          updateTeamLearnerAssignments(
            teamId,
            courseAssignmentUpdates,
            usersToUnassign,
            usersToAssign,
          ),
        );
        setUsersToUnassign([]);
        setUsersToAssign([]);
        setCourseAssignmentUpdates([]);
        setAdjustingCourseAssignments(false);
        setSavedChanges(true);
      } catch (e) {
        console.error(`Couldn't save learners:`, e);
      }
    };

    const cancel = () => {
      setUsersToAssign([]);
      setUsersToUnassign([]);
      setCourseAssignmentUpdates([]);
      setAdjustingCourseAssignments(false);
      setEditingLearnerAssignments(false);
    };

    const onLearnerCourseAssignmentChange = (
      learnerId: string,
      teamId: string,
      course: ICourse,
      unassigning: boolean,
    ) => {
      const existingIndexInUpdates = courseAssignmentUpdates.findIndex(
        (update) => update.courseId === course._id && update.userId === learnerId,
      );
      const newCourseAssignmentUpdates = cloneDeep(courseAssignmentUpdates);
      if (existingIndexInUpdates > -1) {
        newCourseAssignmentUpdates.splice(existingIndexInUpdates, 1);
      } else {
        newCourseAssignmentUpdates.push({
          userId: learnerId,
          courseId: course._id || '',
          teamId: teamId,
          unassigning,
        });
      }
      setCourseAssignmentUpdates(newCourseAssignmentUpdates);
    };

    const handleLearnerAssignmentChange = ({
      assigning,
      source,
      target,
      learner,
    }: {
      assigning: boolean;
      source: IUser[];
      target: IUser[];
      learner: IUser;
    }) => {
      const updatedSource = cloneDeep(source);
      const updatedTarget = cloneDeep(target);

      const sourceIndex = updatedSource.findIndex(
        (sourceLearner) => sourceLearner._id === learner._id,
      );
      if (sourceIndex > -1) {
        updatedSource.splice(sourceIndex, 1);
      }
      const targetIndex = updatedTarget.findIndex(
        (targetLearner) => targetLearner._id === learner._id,
      );
      if (targetIndex === -1) {
        updatedTarget.push(learner);
      }

      const newUsersToAssign = cloneDeep(usersToAssign);
      const newUsersToUnassign = cloneDeep(usersToUnassign);

      const userPresentInAssigned = prevTeamLearners.current?.some(
        (prevLearner) => prevLearner._id === learner._id,
      );
      const userPresentInUnassigned = prevUnassignedLearners.current?.some(
        (prevUnassigned) => prevUnassigned._id === learner._id,
      );

      if (assigning) {
        const indexInUsersToUnassign = newUsersToUnassign.findIndex(
          (userToUnassign) => userToUnassign === learner._id,
        );
        if (indexInUsersToUnassign > -1) {
          newUsersToUnassign.splice(indexInUsersToUnassign, 1);
        }
        if (!userPresentInAssigned) {
          newUsersToAssign.push(learner._id as string);
        }
      }
      if (!assigning) {
        const indexInUsersToAssign = newUsersToAssign.findIndex(
          (userToAssign) => userToAssign === learner._id,
        );
        if (indexInUsersToAssign > -1) {
          newUsersToAssign.splice(indexInUsersToAssign, 1);
        }
        if (!userPresentInUnassigned) {
          newUsersToUnassign.push(learner._id || '');
        }
      }
      setUsersToAssign(newUsersToAssign);
      setUsersToUnassign(newUsersToUnassign);

      return { updatedSource, updatedTarget };
    };

    const onLearnerAssignmentChange = (user: IUser, assigningToTeam: boolean) => {
      const results = handleLearnerAssignmentChange({
        assigning: assigningToTeam,
        source: assigningToTeam ? unassignedLearners : assignedLearners,
        target: assigningToTeam ? assignedLearners : unassignedLearners,
        learner: user,
      });

      setAssignedLearners(assigningToTeam ? results.updatedTarget : results.updatedSource);
      setUnassignedLearners(assigningToTeam ? results.updatedSource : results.updatedTarget);
    };

    const processedAssignedLearners = assignedLearners?.map((teamLearner) => {
      const updatedLearner = cloneDeep(teamLearner);
      const courseUpdatesForUser = courseAssignmentUpdates.filter(
        (assignmentUpdate) => assignmentUpdate.userId === updatedLearner._id,
      );
      const newlyUnassignedCoursesForUser = courseUpdatesForUser.filter(
        (courseUpdate) => courseUpdate.unassigning,
      );

      // Filter out any courses that were previously unassigned, but are no longer
      // (they have been re-assigned)
      updatedLearner.unassignedCourses = updatedLearner.unassignedCourses?.filter(
        (unassignedCourse) =>
          !courseUpdatesForUser.find((updatedUnassignedCourse) => {
            return (
              unassignedCourse.courseId === updatedUnassignedCourse.courseId &&
              unassignedCourse.teamId === updatedUnassignedCourse.teamId &&
              !updatedUnassignedCourse.unassigning
            );
          }),
      );

      if (!updatedLearner.unassignedCourses && newlyUnassignedCoursesForUser.length > 0) {
        updatedLearner.unassignedCourses = [];
      }

      // Add in newly unassigned courses
      newlyUnassignedCoursesForUser.forEach((newlyUnassignedCourse) => {
        const newlyUnassignedCourseTitle = teamCourses.find(
          (teamCourse) => teamCourse._id === newlyUnassignedCourse.courseId,
        )?.title;
        updatedLearner.unassignedCourses?.push({
          courseId: newlyUnassignedCourse.courseId,
          teamId: newlyUnassignedCourse.teamId,
          courseTitle: newlyUnassignedCourseTitle || '',
        });
      });

      updatedLearner.markedForRemoval = false;

      updatedLearner.isChanged = !!usersToAssign.find((userId) => userId === updatedLearner._id);

      return updatedLearner;
    });

    const processedUnassignedLearners = unassignedLearners?.map((unassignedLearner) => {
      const updatedLearner = cloneDeep(unassignedLearner);

      updatedLearner.isChanged = false;
      updatedLearner.markedForRemoval = !!usersToUnassign.find(
        (userId) => userId === updatedLearner._id,
      );

      return updatedLearner;
    });

    if (processedAssignedLearners && teamId) {
      return (
        <FormSection sectionName="Learners">
          <Row className="Learners-tables">
            <Col xs={12} md={{ span: editingLearnerAssignments ? 6 : 12 }}>
              <Table
                activeRowId={selectedUser}
                columns={TeamLearnersHeaders}
                data={processedAssignedLearners}
                hiddenColumns={hiddenColumns}
                tableTitle="Assigned Learners"
                customDataForCell={{
                  coursesAssignedToTeam: teamCourses,
                  learnerAssignedToTeam: true,
                  teamId: teamId,
                  adjustingCourseAssignments: adjustingCourseAssignments,
                  editingLearnerAssignments: editingLearnerAssignments,
                  onLearnerCourseAssignmentChange: onLearnerCourseAssignmentChange,
                  onLearnerAssignmentChange: onLearnerAssignmentChange,
                  dispatch,
                }}
                rowsNotClickable={adjustingCourseAssignments || editingLearnerAssignments}
                onRowClick={(row) => {
                  if (!adjustingCourseAssignments) {
                    setRightPaneOpen(true);
                    updateSearchParams({
                      fields: [
                        {
                          name: 'userId',
                          newValue: row.original._id,
                        },
                      ],
                      searchParams,
                      setSearchParams,
                    });
                  }
                }}
              />
            </Col>
            <Col xs={12} md={6}>
              {editingLearnerAssignments && (
                <Table
                  columns={TeamLearnersHeaders}
                  data={processedUnassignedLearners}
                  hiddenColumns={['courses']}
                  tableTitle="Unassigned Learners"
                  customDataForCell={{
                    editingLearnerAssignments: editingLearnerAssignments,
                    learnerAssignedToTeam: false,
                    onLearnerCourseAssignmentChange: onLearnerCourseAssignmentChange,
                    onLearnerAssignmentChange: onLearnerAssignmentChange,
                  }}
                  rowsNotClickable={adjustingCourseAssignments || editingLearnerAssignments}
                />
              )}
            </Col>
          </Row>
        </FormSection>
      );
    }
    return null;
  },
);

export default TeamLearnersTab;
